<template>
  <MainLayout>
    <div 
      :class="theme === 'dark' ? 'tw-bg-[#151515] tw-text-gray-100' : 'tw-bg-gray-100 tw-text-gray-800'" 
      class="tw-min-h-screen tw-p-4 md:tw-p-8"
      :data-theme="theme"
    >
      <div class="tw-mb-6 md:tw-mb-8">
        <h1 :class="theme === 'dark' ? 'tw-text-gray-100' : 'tw-text-gray-800'" class="tw-text-xl md:tw-text-2xl tw-font-bold">
          Relatório de Logs dos Usuários - {{ unidade.nome || 'Unidade' }}
        </h1>
      </div>

      <div class="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-gap-8">
        <div 
          :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-border-gray-800' : 'tw-bg-white tw-border-gray-100'" 
          class="tw-rounded-2xl tw-p-5 md:tw-p-8 tw-shadow-2xl tw-border"
        >
          <div class="tw-flex tw-items-center tw-gap-4 tw-mb-6 md:tw-mb-8">
            <div :class="theme === 'dark' ? 'tw-bg-blue-500/10' : 'tw-bg-blue-50'" class="tw-p-3 tw-rounded-xl">
              <i class="fa-solid fa-filter tw-text-blue-500 tw-text-lg"></i>
            </div>
            <h2 class="tw-text-base md:tw-text-lg tw-font-bold">Filtros de Busca</h2>
          </div>

          <form @submit.prevent="gerarRelatorio(1)">
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4 md:tw-gap-6">
              <div class="tw-space-y-2">
                <label class="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Período</label>
                <BaseSelect v-model="data_pre_definida" :options="periodoOptions" option-label="label" option-value="value" />
              </div>
              <div class="tw-space-y-2">
                <label class="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Início</label>
                <input type="date" v-model="dataInicio" class="tw-custom-input" />
              </div>
              <div class="tw-space-y-2">
                <label class="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Final</label>
                <input type="date" v-model="dataFinal" class="tw-custom-input" />
              </div>
              <div class="tw-space-y-2">
                <label class="tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Por Página</label>
                <BaseSelect v-model="per_page" :options="perPageOptions">
                  <template #option="{ option }">{{ option }} itens</template>
                  <template #value="{ selected }">{{ selected }} itens</template>
                </BaseSelect>
              </div>
            </div>

            <div class="tw-mt-8 tw-flex tw-justify-end tw-gap-4">
              <button 
                type="submit" 
                :disabled="loadingDados"
                class="tw-px-10 tw-py-3 tw-rounded-xl tw-bg-green-600 tw-text-white tw-text-sm tw-font-bold hover:tw-bg-green-700 tw-shadow-lg tw-shadow-green-600/20 tw-border-none disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              >
                <span v-if="!loadingDados">Visualizar Relatório</span>
                <span v-else class="tw-flex tw-items-center tw-gap-2">
                  <svg class="tw-animate-spin tw-h-4 tw-w-4 tw-text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processando...
                </span>
              </button>
            </div>
          </form>
        </div>

        <div 
          :class="theme === 'dark' ? 'tw-bg-[#1a1d21] tw-border-gray-800' : 'tw-bg-white tw-border-gray-100'" 
          class="tw-rounded-2xl tw-p-0 tw-shadow-2xl tw-border tw-overflow-hidden tw-relative tw-min-h-[300px]"
        >
          <div class="tw-p-5 md:tw-p-8 tw-border-b tw-border-gray-800/10 tw-flex tw-items-center tw-gap-4">
            <div :class="theme === 'dark' ? 'tw-bg-green-500/10' : 'tw-bg-green-50'" class="tw-p-3 tw-rounded-xl">
              <i class="fa-solid fa-file-invoice tw-text-green-500 tw-text-lg"></i>
            </div>
            <h2 :class="theme === 'dark' ? 'tw-text-gray-100' : 'tw-text-gray-800'" class="tw-text-base md:tw-text-lg tw-font-bold">
              Relatório de Atividades dos Usuários
            </h2>
          </div>

          <transition name="fade">
            <div v-if="loadingDados" class="tw-absolute tw-inset-0 tw-z-10 tw-bg-inherit tw-flex tw-flex-col tw-items-center tw-justify-center">
              <svg class="tw-animate-spin tw-h-10 tw-w-10 tw-text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="tw-opacity-20" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="tw-opacity-100" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span class="tw-mt-4 tw-text-xs tw-font-bold tw-uppercase tw-tracking-widest tw-opacity-50">Sincronizando logs...</span>
            </div>
          </transition>

          <div v-if="logsData && logsData.length > 0" :class="{'tw-opacity-20': loadingDados}" class="tw-block tw-overflow-x-auto">
            <table class="tw-w-full tw-text-left">
              <thead>
                <tr :class="theme === 'dark' ? 'tw-bg-black/20' : 'tw-bg-gray-50/50'">
                  <th class="xs:tw-px-2 md:tw-px-8 tw-py-4 tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Data e Hora</th>
                  <th class="xs:tw-px-2 md:tw-px-8 tw-py-4 tw-text-[10px] tw-font-bold tw-text-gray-400 tw-uppercase">Descrição</th>
                </tr>
              </thead>
              <tbody class="tw-divide-y tw-divide-gray-800/10">
                <tr v-for="(item, index) in logsData" :key="index" class="hover:tw-bg-black/5">
                  <td class="xs:tw-px-2 md:tw-px-8 tw-py-5 tw-text-xs tw-font-mono tw-text-gray-500">{{ dataFormatadaLonga(item.created_at) }}</td>
                  <td class="xs:tw-px-2 md:tw-px-8 tw-py-5 tw-text-sm tw-leading-relaxed">{{ item.descricao }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="!loadingDados && logsData && logsData.length > 0" class="tw-px-6 tw-py-6 tw-bg-black/5 tw-border-t tw-border-gray-800/10">
            <div class="tw-flex tw-flex-col md:tw-flex-row tw-items-center tw-justify-between tw-gap-4">
              <div class="tw-text-xs tw-font-medium tw-opacity-60">
                Mostrando <span class="tw-font-bold">{{ (pagination.current_page - 1) * per_page + 1 }}</span> a 
                <span class="tw-font-bold">{{ Math.min(pagination.current_page * per_page, pagination.total) }}</span> de 
                <span class="tw-font-bold">{{ pagination.total }}</span> resultados
              </div>

              <nav v-if="pagination.last_page > 1" class="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-1">
                <button 
                  @click="gerarRelatorio(pagination.current_page - 1)"
                  :disabled="pagination.current_page === 1"
                  class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-black/10 disabled:tw-opacity-10 tw-border-none tw-bg-transparent"
                >
                  <i class="fa-solid fa-chevron-left tw-text-[10px]"></i>
                </button>

                <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-1">
                  <button 
                    v-for="link in filteredPaginationLinks" 
                    :key="link.label"
                    @click="handlePaginationClick(link)"
                    :class="[
                      'tw-w-9 tw-h-9 tw-text-xs tw-font-bold tw-rounded-lg tw-border-none',
                      link.active ? 'tw-bg-green-600 tw-text-white' : 'tw-bg-transparent hover:tw-bg-black/10 tw-opacity-60'
                    ]"
                    v-html="link.label"
                  ></button>
                </div>

                <button 
                  @click="gerarRelatorio(pagination.current_page + 1)"
                  :disabled="pagination.current_page === pagination.last_page"
                  class="tw-w-9 tw-h-9 tw-flex tw-items-center tw-justify-center tw-rounded-lg hover:tw-bg-black/10 disabled:tw-opacity-10 tw-border-none tw-bg-transparent"
                >
                  <i class="fa-solid fa-chevron-right tw-text-[10px]"></i>
                </button>
              </nav>
            </div>
          </div>

          <div v-else-if="!loadingDados && (!logsData || logsData.length === 0)" class="tw-py-16 tw-text-center">
            <p class="tw-text-gray-500 tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-opacity-50">Nenhum registro encontrado.</p>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>

<script>
/* eslint-disable */
import MainLayout from "@/components/layouts/v2/MainLayout.vue";
import { useToast } from "vue-toastification";
import { mapState } from 'vuex';
import BaseSelect from "@/components/ui/v2/BaseSelect.vue";

export default {
    name: 'RelatorioLogs',
    components: { MainLayout, BaseSelect },
    data() {
        const today = new Date().toISOString().split('T')[0];
        return {
            logsData: [],
            pagination: { current_page: 1, last_page: 1, total: 0, links: [] },
            perPageOptions: [10, 25, 50, 100],
            periodoOptions: [
                { label: 'Selecione...', value: 'null' },
                { label: 'Dia atual', value: '0' },
                { label: 'Últimos 7 dias', value: '2' },
                { label: 'Últimos 30 dias', value: '4' },
            ],
            per_page: 10,
            dataInicio: today,
            dataFinal: today,
            data_pre_definida: "null",
            loadingDados: false,
            toast: useToast()
        };
    },
    computed: {
        ...mapState({
            unidade: state => state.unidade.unidade || {},
            theme: state => state.ui.theme
        }),
        filteredPaginationLinks() {
            return this.pagination.links.filter(l => !isNaN(l.label) || l.label === '...');
        }
    },
    methods: {
        getDatasPredefinidas(periodo) {
            const today = new Date();
            let inicio;
            let fim = today.toISOString().split('T')[0];
            switch (periodo) {
                case "0": inicio = fim; break;
                case "1": {
                    const d = new Date(); d.setDate(d.getDate() - 1);
                    inicio = d.toISOString().split('T')[0]; fim = inicio;
                    break;
                }
                case "2": {
                    const d = new Date(); d.setDate(d.getDate() - 7);
                    inicio = d.toISOString().split('T')[0]; break;
                }
                case "4": {
                    const d = new Date(); d.setDate(d.getDate() - 30);
                    inicio = d.toISOString().split('T')[0]; break;
                }
                case "5": {
                    inicio = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
                    break;
                }
            }
            return { dataInicio: inicio, dataFinal: fim };
        },
        async gerarRelatorio(page = 1) {
            this.loadingDados = true;
            try {
                const unidadeId = this.$route.params.unidade || this.unidade.id;
                const response = await this.$store.dispatch("unidades/fetchLogsDataRelatorio", {
                    unidadeId, dataInicio: this.dataInicio, dataFim: this.dataFinal,
                    page, per_page: this.per_page
                });

                if (response && response.data) {
                    this.logsData = response.data;
                    this.pagination = {
                        current_page: response.current_page || 1,
                        last_page: response.last_page || 1,
                        total: response.total || 0,
                        links: response.links || []
                    };
                }
            } catch (error) {
                this.toast.error("Erro ao carregar dados.");
            } finally {
                // Pequeno delay para evitar flicker em conexões rápidas
                setTimeout(() => { this.loadingDados = false; }, 300);
            }
        },
        handlePaginationClick(link) {
            if (!link.url || link.active || link.label === '...') return;
            const url = new URL(link.url);
            this.gerarRelatorio(url.searchParams.get('page'));
        },
        limparFiltros() {
            this.data_pre_definida = "null";
            this.logsData = [];
            this.pagination.total = 0;
        },
        dataFormatadaLonga(data) {
            return new Date(data).toLocaleString("pt-BR");
        }
    },
};
</script>

<style scoped>
.tw-custom-input {
    @apply tw-w-full tw-border-none tw-rounded-xl tw-p-3 md:tw-p-4 tw-text-sm tw-outline-none;
}

[data-theme="dark"] .tw-custom-input {
    background-color: rgba(39, 43, 47, 0.35);
    color: #f3f4f6;
}

[data-theme="light"] .tw-custom-input {
    background-color: #f9fafb;
    color: #1f2937;
    border: 1px solid #f3f4f6;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>